import type {
  Channel,
  DmChannel,
  Friendship,
  Guild,
  Invite,
  Member,
  Message,
  Role,
  User,
} from '@callzone/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

let baseUrl = '';
let authToken: string | null = null;

export function configureApi(url: string, token: string | null) {
  baseUrl = url.replace(/\/+$/, '');
  authToken = token;
}

export function getBaseUrl() {
  return baseUrl;
}

export function gatewayUrl() {
  return `${baseUrl.replace(/^http/, 'ws')}/gateway`;
}

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; token?: string | null } = {},
): Promise<T> {
  const token = options.token !== undefined ? options.token : authToken;
  const res = await fetch(baseUrl + path, {
    method: options.method ?? 'GET',
    headers: {
      ...(options.body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let payload: any = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    throw new ApiError(res.status, 'Resposta invalida do servidor.');
  }

  if (!res.ok) throw new ApiError(res.status, payload?.error ?? 'Falha na requisicao.');
  return payload as T;
}

export interface GuildBundle {
  guild: Guild;
  channels: Channel[];
  roles: Role[];
  members: Member[];
  permissions: string;
}

export const api = {
  status: (url: string) =>
    fetch(`${url.replace(/\/+$/, '')}/api/status`).then((r) => {
      if (!r.ok) throw new ApiError(r.status, 'Servidor nao respondeu.');
      return r.json() as Promise<{ initialized: boolean; livekitUrl: string }>;
    }),

  bootstrap: (body: {
    code: string;
    nickname: string;
    password: string;
    guildName: string;
    deviceLabel: string;
  }) =>
    request<{ token: string; user: User; guild: Guild }>('/api/auth/bootstrap', {
      method: 'POST',
      body,
      token: null,
    }),

  login: (body: { nickname: string; password: string; deviceLabel: string }) =>
    request<{ token: string; user: User }>('/api/auth/login', {
      method: 'POST',
      body,
      token: null,
    }),

  changePassword: (body: { currentPassword?: string; password: string }) =>
    request<void>('/api/auth/password', { method: 'POST', body }),

  previewInvite: (code: string) =>
    request<{ guild: { id: string; name: string; iconColor: string }; memberCount: number }>(
      `/api/invites/${encodeURIComponent(code)}`,
      { token: null },
    ),

  redeem: (
    body: {
      code: string;
      nickname?: string;
      fullName?: string;
      password?: string;
      deviceLabel: string;
    },
    token?: string | null,
  ) =>
    request<{ token: string | null; user: User; guild: Guild }>('/api/auth/redeem', {
      method: 'POST',
      body,
      token: token ?? null,
    }),

  me: () => request<{ user: User; deviceId: string }>('/api/auth/me'),
  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),
  devices: () =>
    request<{
      devices: Array<{
        id: string;
        label: string;
        createdAt: number;
        lastSeen: number;
        current: boolean;
      }>;
    }>('/api/auth/devices'),
  revokeDevice: (id: string) => request<void>(`/api/auth/devices/${id}`, { method: 'DELETE' }),

  guilds: () => request<{ guilds: GuildBundle[] }>('/api/guilds'),
  createGuild: (name: string) => request<{ guild: Guild }>('/api/guilds', { method: 'POST', body: { name } }),
  updateGuild: (guildId: string, body: { name?: string; iconColor?: string }) =>
    request<{ guild: Guild }>(`/api/guilds/${guildId}`, { method: 'PATCH', body }),

  createChannel: (
    guildId: string,
    body: { name: string; type: 'text' | 'voice'; topic?: string; userLimit?: number },
  ) => request<{ channel: Channel }>(`/api/guilds/${guildId}/channels`, { method: 'POST', body }),
  updateChannel: (
    channelId: string,
    body: { name?: string; topic?: string | null; userLimit?: number },
  ) => request<{ channel: Channel }>(`/api/channels/${channelId}`, { method: 'PATCH', body }),
  deleteChannel: (channelId: string) =>
    request<void>(`/api/channels/${channelId}`, { method: 'DELETE' }),

  setOverwrite: (channelId: string, roleId: string, allow: string, deny: string) =>
    request<unknown>(`/api/channels/${channelId}/overwrites/${roleId}`, {
      method: 'PUT',
      body: { allow, deny },
    }),
  overwrites: (channelId: string) =>
    request<{ overwrites: Array<{ roleId: string; allow: string; deny: string }> }>(
      `/api/channels/${channelId}/overwrites`,
    ),

  createRole: (guildId: string, body: { name: string; color?: string; permissions: string; hoist?: boolean }) =>
    request<{ role: Role }>(`/api/guilds/${guildId}/roles`, { method: 'POST', body }),
  updateRole: (
    roleId: string,
    body: { name?: string; color?: string; permissions?: string; hoist?: boolean },
  ) => request<{ role: Role }>(`/api/roles/${roleId}`, { method: 'PATCH', body }),
  deleteRole: (roleId: string) => request<void>(`/api/roles/${roleId}`, { method: 'DELETE' }),

  addRole: (guildId: string, userId: string, roleId: string) =>
    request<{ member: Member }>(`/api/guilds/${guildId}/members/${userId}/roles/${roleId}`, {
      method: 'PUT',
    }),
  removeRole: (guildId: string, userId: string, roleId: string) =>
    request<{ member: Member }>(`/api/guilds/${guildId}/members/${userId}/roles/${roleId}`, {
      method: 'DELETE',
    }),
  kickMember: (guildId: string, userId: string) =>
    request<void>(`/api/guilds/${guildId}/members/${userId}`, { method: 'DELETE' }),

  invites: (guildId: string) => request<{ invites: Invite[] }>(`/api/guilds/${guildId}/invites`),
  createInvite: (
    guildId: string,
    body: { maxUses?: number; expiresInHours?: number; grantRoleId?: string | null },
  ) => request<{ invite: Invite }>(`/api/guilds/${guildId}/invites`, { method: 'POST', body }),
  revokeInvite: (code: string) => request<void>(`/api/invites/${code}`, { method: 'DELETE' }),

  messages: (channelId: string, before?: string) =>
    request<{ messages: Message[] }>(
      `/api/channels/${channelId}/messages${before ? `?before=${before}` : ''}`,
    ),
  sendMessage: (channelId: string, content: string) =>
    request<{ message: Message }>(`/api/channels/${channelId}/messages`, {
      method: 'POST',
      body: { content },
    }),
  editMessage: (messageId: string, content: string) =>
    request<{ message: Message }>(`/api/messages/${messageId}`, {
      method: 'PATCH',
      body: { content },
    }),
  deleteMessage: (messageId: string) =>
    request<void>(`/api/messages/${messageId}`, { method: 'DELETE' }),

  voiceToken: (channelId: string) =>
    request<{
      token: string;
      url: string;
      room: string;
      canPublish: Array<'microphone' | 'camera' | 'screen_share' | 'screen_share_audio'>;
    }>(`/api/channels/${channelId}/voice-token`, { method: 'POST' }),
  leaveVoice: () => request<void>('/api/voice/leave', { method: 'POST' }),
  moderateVoice: (
    guildId: string,
    userId: string,
    body: { serverMute?: boolean; disconnect?: boolean; moveToChannelId?: string },
  ) => request<unknown>(`/api/guilds/${guildId}/members/${userId}/voice`, { method: 'POST', body }),

  // ----------------------------------------------- amizade e conversa privada

  amizades: () => request<{ amizades: Friendship[] }>('/api/amizades'),
  pedirAmizade: (alvo: { apelido?: string; userId?: string }) =>
    request<{ amizade: Friendship }>('/api/amizades', { method: 'POST', body: alvo }),
  aceitarAmizade: (userId: string) =>
    request<{ amizade: Friendship }>(`/api/amizades/${userId}/aceitar`, { method: 'POST' }),
  removerAmizade: (userId: string) =>
    request<void>(`/api/amizades/${userId}`, { method: 'DELETE' }),

  conversas: () => request<{ conversas: DmChannel[] }>('/api/privado'),
  abrirConversa: (userId: string) =>
    request<{ conversa: DmChannel }>(`/api/privado/${userId}`, { method: 'POST' }),
  mensagensPrivadas: (channelId: string, before?: string) =>
    request<{ mensagens: Message[] }>(
      `/api/privado/${channelId}/mensagens${before ? `?before=${before}` : ''}`,
    ),
  enviarPrivada: (channelId: string, content: string) =>
    request<{ mensagem: Message }>(`/api/privado/${channelId}/mensagens`, {
      method: 'POST',
      body: { content },
    }),
  ligacaoPrivada: (channelId: string) =>
    request<{ token: string; url: string; room: string; canPublish: string[] }>(
      `/api/privado/${channelId}/ligacao`,
      { method: 'POST' },
    ),

  // ------------------------------------------------------------- perfil

  atualizarPerfil: (body: { nickname?: string; fullName?: string; accentColor?: string }) =>
    request<{ user: User }>('/api/perfil', { method: 'PATCH', body }),

  /**
   * A imagem vai crua no corpo, com o tipo no content-type. Multipart exigiria
   * um plugin no servidor so para um campo.
   */
  enviarFoto: async (blob: Blob) => {
    const res = await fetch(`${baseUrl}/api/perfil/foto`, {
      method: 'PUT',
      headers: {
        'content-type': blob.type || 'image/webp',
        ...(authToken ? { authorization: `Bearer ${authToken}` } : {}),
      },
      body: blob,
    });
    if (!res.ok) {
      const texto = await res.text();
      let mensagem = 'Falha ao enviar a foto.';
      try {
        mensagem = JSON.parse(texto)?.error ?? mensagem;
      } catch {
        // resposta sem JSON: fica a mensagem padrao
      }
      throw new ApiError(res.status, mensagem);
    }
    return (await res.json()) as { avatarVersion: number };
  },

  removerFoto: () => request<void>('/api/perfil/foto', { method: 'DELETE' }),

  /** Apelidos particulares: so voce ve os que voce definiu. */
  aliases: () => request<{ aliases: Record<string, string> }>('/api/aliases'),
  setAlias: (userId: string, alias: string | null) =>
    request<{ alias: string | null }>(`/api/aliases/${userId}`, { method: 'PUT', body: { alias } }),
};
